import { ChangeEvent, KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react'

import { MAX_REVIEW_PHOTOS, MAX_REVIEW_VIDEOS } from './constants'

export type ReviewAttachment = {
	file?: File
	name: string
	preview: string
	type: 'image' | 'video'
	isExisting?: boolean
	mediaUuid?: string
}

export type ReviewInitialAttachment = Omit<ReviewAttachment, 'file'>

const revokeAttachmentPreview = (attachment: ReviewAttachment) => {
	if (!attachment.isExisting) URL.revokeObjectURL(attachment.preview)
}

export const useReviewAttachments = () => {
	const [attachments, setAttachments] = useState<ReviewAttachment[]>([])
	const [attachmentError, setAttachmentError] = useState('')
	const fileInputRef = useRef<HTMLInputElement>(null)
	const attachmentsRef = useRef(attachments)
	attachmentsRef.current = attachments

	useEffect(
		() => () => {
			attachmentsRef.current.forEach(revokeAttachmentPreview)
		},
		[]
	)

	const addFiles = (event: ChangeEvent<HTMLInputElement>) => {
		let photoCount = attachments.filter(attachment => attachment.type === 'image').length
		let videoCount = attachments.filter(attachment => attachment.type === 'video').length
		let hasRejectedFiles = false
		const files = Array.from(event.target.files ?? []).filter(file => {
			if (file.type.startsWith('video/')) {
				if (videoCount >= MAX_REVIEW_VIDEOS) {
					hasRejectedFiles = true
					return false
				}
				videoCount += 1
				return true
			}
			if (!file.type.startsWith('image/') || photoCount >= MAX_REVIEW_PHOTOS) {
				hasRejectedFiles = true
				return false
			}
			photoCount += 1
			return true
		})
		setAttachmentError(
			hasRejectedFiles ? `Можно загрузить не более ${MAX_REVIEW_PHOTOS} фото и ${MAX_REVIEW_VIDEOS} видео.` : ''
		)

		setAttachments(items => [
			...items,
			...files.map(file => ({
				file,
				name: file.name,
				preview: URL.createObjectURL(file),
				type: file.type.startsWith('video/') ? ('video' as const) : ('image' as const)
			}))
		])
		event.target.value = ''
	}

	const removeAttachment = (index: number) => {
		setAttachmentError('')
		setAttachments(items => {
			const attachment = items[index]
			if (attachment) revokeAttachmentPreview(attachment)
			return items.filter((_, itemIndex) => itemIndex !== index)
		})
	}

	const resetAttachments = useCallback((initialAttachments: ReviewInitialAttachment[] = []) => {
		setAttachmentError('')
		setAttachments(items => {
			items.forEach(revokeAttachmentPreview)
			return initialAttachments
		})
		if (fileInputRef.current) fileInputRef.current.value = ''
	}, [])

	const openFileDialog = () => fileInputRef.current?.click()

	const handleUploadKeyDown = (event: KeyboardEvent<HTMLElement>) => {
		if (event.key !== 'Enter' && event.key !== ' ') return
		event.preventDefault()
		openFileDialog()
	}

	return {
		attachments,
		attachmentError,
		fileInputRef,
		addFiles,
		removeAttachment,
		resetAttachments,
		openFileDialog,
		handleUploadKeyDown
	}
}
